import React, { useState, useEffect } from 'react';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import { generateText3D } from '../utils/geometry';

// Default fonts (we'll use Three.js built-in fonts)
const FONTS = [
  { id: 'helvetiker', name: 'Helvetiker', url: 'https://threejs.org/examples/fonts/helvetiker_regular.typeface.json' },
  { id: 'optimer', name: 'Optimer', url: 'https://threejs.org/examples/fonts/optimer_regular.typeface.json' },
  { id: 'gentilis', name: 'Gentilis', url: 'https://threejs.org/examples/fonts/gentilis_regular.typeface.json' },
  { id: 'droid_sans', name: 'Droid Sans', url: 'https://threejs.org/examples/fonts/droid/droid_sans_regular.typeface.json' },
  { id: 'droid_serif', name: 'Droid Serif', url: 'https://threejs.org/examples/fonts/droid/droid_serif_regular.typeface.json' }
];

export default function TextGenerator({ onAddText, onError }) {
  const [text, setText] = useState('Cadify');
  const [selectedFont, setSelectedFont] = useState(FONTS[0]);
  const [loadedFont, setLoadedFont] = useState(null);
  const [loading, setLoading] = useState(false);
  const [textColor, setTextColor] = useState('#9bbcff');
  
  const [params, setParams] = useState({
    size: 16,
    height: 3,
    curveSegments: 12,
    bevelEnabled: true,
    bevelThickness: 0.6,
    bevelSize: 0.35,
    bevelSegments: 3
  });

  useEffect(() => {
    loadFont(selectedFont);
  }, [selectedFont]);

  const loadFont = async (fontConfig) => {
    setLoading(true);
    try {
      const loader = new FontLoader();
      loader.load(
        fontConfig.url,
        (font) => {
          setLoadedFont(font);
          setLoading(false);
        },
        undefined,
        (error) => {
          console.error('Font loading error:', error);
          onError(new Error(`Failed to load font: ${fontConfig.name}`));
          setLoading(false);
        }
      );
    } catch (error) {
      onError(error);
      setLoading(false);
    }
  };

  const handleParamChange = (key, value) => {
    setParams(prev => ({
      ...prev,
      [key]: typeof prev[key] === 'boolean' ? value : parseFloat(value) || 0
    }));
  };

  const handleGenerate = async () => {
    if (!text.trim()) {
      onError(new Error('Please enter some text'));
      return;
    }

    if (!loadedFont) {
      onError(new Error('Font not loaded yet'));
      return;
    }

    try {
      const color = parseInt(textColor.replace('#', '0x'));
      const result = await generateText3D(text, {
        ...params,
        color,
        font: loadedFont
      });
      result.mesh.name = `Text: ${text.slice(0, 24)}`;
      result.mesh.userData = {
        ...result.mesh.userData,
        type: '3D Text',
        name: result.mesh.name
      };

      onAddText(result);
    } catch (error) {
      onError(error);
    }
  };

  return (
    <div className="panel">
      <div className="panel-header">3D Text</div>

      <div className="space-y-3">
        <div>
          <label className="text-xs text-neutral-400 mb-1 block">Text</label>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Enter text..."
            className="input w-full"
            maxLength={50}
          />
        </div>

        <div>
          <label className="text-xs text-neutral-400 mb-1 block">Font</label>
          <select
            value={selectedFont.id}
            onChange={(e) => setSelectedFont(FONTS.find(f => f.id === e.target.value))}
            className="input w-full"
            disabled={loading}
          >
            {FONTS.map(font => (
              <option key={font.id} value={font.id}>
                {font.name}
              </option>
            ))}
          </select>
          {loading && (
            <div className="text-xs text-neutral-500 mt-1 flex items-center gap-2">
              <span className="spinner"></span>
              Loading font...
            </div>
          )}
        </div>

        <div className="grid-2">
          <div>
            <label className="text-xs text-neutral-400 mb-1 block">Size</label>
            <input
              type="number"
              step="0.1"
              min="0.1"
              value={params.size}
              onChange={(e) => handleParamChange('size', e.target.value)}
              className="input w-full"
            />
          </div>
          <div>
            <label className="text-xs text-neutral-400 mb-1 block">Depth</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={params.height}
              onChange={(e) => handleParamChange('height', e.target.value)}
              className="input w-full"
            />
          </div>
        </div>

        <div>
          <label className="flex items-center gap-2 text-xs text-neutral-400">
            <input
              type="checkbox"
              checked={params.bevelEnabled}
              onChange={(e) => handleParamChange('bevelEnabled', e.target.checked)}
            />
            Enable Bevel
          </label>
        </div>

        {params.bevelEnabled && (
          <div className="grid-2 fade-in">
            <div>
              <label className="text-xs text-neutral-400 mb-1 block">Bevel Thickness</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={params.bevelThickness}
                onChange={(e) => handleParamChange('bevelThickness', e.target.value)}
                className="input w-full"
              />
            </div>
            <div>
              <label className="text-xs text-neutral-400 mb-1 block">Bevel Size</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={params.bevelSize}
                onChange={(e) => handleParamChange('bevelSize', e.target.value)}
                className="input w-full"
              />
            </div>
          </div>
        )}

        <div>
          <label className="text-xs text-neutral-400 mb-1 block">Color</label>
          <input
            type="color"
            value={textColor}
            onChange={(e) => setTextColor(e.target.value)}
            className="w-full h-10"
          />
        </div>

        <button
          onClick={handleGenerate}
          disabled={loading || !loadedFont || !text.trim()}
          className={`btn w-full ${loading || !loadedFont || !text.trim() ? 'btn-disabled' : 'btn-primary'}`}
        >
          {loading ? 'Loading Font...' : 'Generate 3D Text'}
        </button>
      </div>
    </div>
  );
}

